import type { ModuleSnapshot } from '@ctcells/contract'
import { enumLabel, t } from '@ctcells/i18n'

import { cx } from '../../atoms/cx.ts'
import { ArmFigure } from '../../figures/ArmFigure.tsx'

import { jointValues, monitoringDeviceName } from './monitoringModel.ts'
import { ROBOT_POSES, robotMoving, robotPhase } from './robotMotionModel.ts'
import { robotRun } from './robotTransferModel.ts'

import './robotArm.css'

/**
 * 6축 팔 그림 — **관절값이 오면 그 자세, 안 오면 서버 동작(시퀀스 · 상태 · 파지)으로 고른 «도식 자세»**(eric 2026-10-07 「6축 로봇 왜 전혀
 * 안움직여?」 · 「움직임을 줘...」 — 2026-10-06 「기준 자세 고정」 결정을 eric 이 뒤집었다).
 *
 * 🔴 **그래도 지어내지 않는다.** 도식 자세는 서버가 1Hz 로 준 값(`current_execution.sequence` · `state` · `grips`)의 함수다
 *    (`robotMotionModel.ts`) — 다음 단계를 짐작하거나 시간을 재서 움직이지 않는다. 프레임이 바뀌면 그때 마디가 토큰 시간만큼 옮겨 가고
 *    (`ArmFigure` 마디 회전 묶음), 이동 단계에서만 좌우로 흔들린다. 관절값이 없는 동안은 점선(「아직 없음」)이고 그 사실을 글자로 적는다 —
 *    대기면 「자세 미발행 — 기준 자세 아이콘」, 동작 중이면 「도식 자세 — 모듈로 이동」.
 * 🔴 파지(쥠/빈손)는 응답 값(`grips[].state`)이라 자세와 무관하게 그대로 그린다.
 * ⚠ 이 컴포넌트는 콘솔 이송 띠 · 장비 판(`FocusFigure` 의 `robot` 갈래 — 장비 관점 앱 · 패드도 거친다)이 같이 쓴다.
 */
export interface RobotArmProps {
  snapshot: ModuleSnapshot | undefined
  /** 「자세 미발행」 글자를 그림 아래에 세운다. 띠처럼 글자를 다른 자리에 두는 곳은 끈다 */
  note?: boolean
  className?: string | undefined
  /** 그림을 접근성 트리에서 뺀다 — 같은 말(이름 · 「자세 미발행」)을 옆 글자가 이미 하는 자리(콘솔 이송 띠의 단추 안) */
  decorative?: boolean
  /** 쥔 트레이를 늘 수평으로(웨이퍼 핸들러) — 6축 가로 라인(`ArmFigure` `heldLevel`) */
  heldLevel?: boolean
  /** 트레이를 넣는 중 · 빼는 중(수평 미끄러짐) */
  heldMotion?: 'in' | 'out' | undefined
}

export function RobotArm({
  snapshot,
  note = true,
  className,
  decorative = false,
  heldLevel = false,
  heldMotion,
}: RobotArmProps) {
  const joints = jointValues(snapshot)
  const holding = (snapshot?.grips ?? []).some((grip) => grip.state === 'holding')
  /* 그림 접근 이름 — 모니터링 판의 이름 「운송 로봇」(eric 2026-10-13 · `monitoringDeviceName`) */
  const name = monitoringDeviceName('robot')
  const unknown = joints === undefined
  const phase = robotPhase(snapshot, snapshot?.stale === true)
  const moving = robotMoving(phase)
  /* 도식 자세의 꼬리 — 서버 시퀀스 이름(사전) 또는 모듈 상태 이름. 이름을 짓지 않는다 */
  const sequence = robotRun(snapshot)?.sequence
  const action =
    sequence !== undefined
      ? enumLabel('enum.robotSequence', sequence)
      : enumLabel('enum.moduleState', snapshot?.state ?? '')
  const noteText =
    phase === 'idle'
      ? t('monitoring.robot.pose.none')
      : t('monitoring.robot.pose.schematic', { action })
  return (
    <span
      className={cx('robotarm', unknown && 'robotarm--nopose', className)}
      data-phase={phase}
      {...(decorative ? { 'aria-hidden': true } : {})}
    >
      <ArmFigure
        heldLevel={heldLevel}
        heldMotion={heldMotion}
        holding={holding}
        jointLabels={!unknown}
        joints={joints ?? ROBOT_POSES[phase]}
        label={unknown ? `${name} — ${noteText}` : name}
        moving={unknown && moving}
      />
      {unknown && note ? <span className="robotarm__note">{noteText}</span> : null}
    </span>
  )
}
